import 'project/polyfill'
import Link from 'next/link'
import classNames from 'classnames'
import ReactPlayer from 'react-player'
import React, { FC, useState } from 'react'
import { ItemListElements } from 'next-seo/lib/types'

import SeoContainer, {
  SeoContainerProps,
} from 'components/util/containers/SeoContainer'
import { Utils } from 'common/utils'
import Strings from 'project/localisation'
import { getPlans } from 'common/planData'
import { createClient } from '../prismicio'
import { TPlanSummary } from 'common/cms-types'
import HoxtonMixLogo from 'components/icons/HoxtonMixLogo'
import { generateSrcSet } from 'project/image-proxy-web'
import useWindowSize from 'components/util/useWindowSize'
import { I18NConfig } from 'next/dist/server/config-shared'
import SolidStateGroup from 'components/icons/SolidStateGroup'
import SquareCircle from 'components/icons/SquareCircle'
import { NextPageWithLayout } from '../types/nextPageWithLayout'
import PageContainer from 'components/util/containers/PageContainer'
import { homeBreadcrumb } from 'components/util/containers/Breadcrumbs'

export type PageType = {
  plans: TPlanSummary[]
}

type SectionProps = {
  title: string | React.ReactNode
  description: string | React.ReactNode
  subDescription: string | React.ReactNode
  image: {
    src: string
    alt: string
    width: number
    height: number
  }
  imagePosition: 'left' | 'right'
  contentContainerClassName?: string
}

const Section: FC<SectionProps> = ({
  contentContainerClassName,
  description,
  image,
  imagePosition,
  subDescription,
  title,
}) => {
  const size = useWindowSize()
  return (
    <div
      className={classNames('d-flex flex-lg-row flex-column', {
        'flex-lg-row-reverse': imagePosition === 'left',
      })}
    >
      <div
        className={classNames(
          'col-lg-6 col-12 d-flex flex-column justify-content-center',
          contentContainerClassName,
        )}
      >
        <h2 className='fs-fluid-36-64 mb-lg-7'>{title}</h2>
        <p className='fs-fluid-32-52 fw-semibold lh-lg-108 lh-140 mb-lg-10 mb-n8'>
          {description}
        </p>

        {size.isLgOrHigher && (
          <p className='fs-fluid-20-28 fw-semibold lh-lg-157 lh-123'>
            {subDescription}
          </p>
        )}
      </div>
      <div className='col-lg-6 col-12'>
        <img
          loading='lazy'
          {...generateSrcSet(image.src, image.width, image.height)}
          alt={image.alt}
          className='w-100'
        />
        {size.isMdOrLower && (
          <div className='fs-fluid-20-28 fw-semibold lh-lg-157 lh-123'>
            {subDescription}
          </div>
        )}
      </div>
    </div>
  )
}

const Partner = ({
  href,
  icon,
  name,
}: {
  icon: React.ReactNode
  name: string
  href: string
}) => {
  return (
    <div className='d-flex flex-column'>
      <div className='max-w-100'>{icon}</div>
      <Link
        className='text-decoration-none text-center text-lg-start'
        href={href}
      >
        {name}
      </Link>
    </div>
  )
}

const Page: NextPageWithLayout<PageType> = ({ plans }) => {
  const seoProps: SeoContainerProps['seoProps'] = {
    description:
      'Established in 2010, The Hoxton Mix is a leading virtual office provider headquartered in London, UK. We empower SMEs worldwide with innovative SaaS solutions.',
    openGraph: {
      url: 'https://www.hoxtonmix.com/about-us/',
    },
    title: 'About The Hoxton Mix',
  }

  const breadcrumb: ItemListElements[] = [
    homeBreadcrumb()[0],
    {
      item: 'https://www.hoxtonmix.com/au/contact-us/',
      name: `${Strings.contactUs}`,
      position: 2,
    },
  ]

  const size = useWindowSize()
  const [playing, setPlaying] = useState(false)

  return (
    <SeoContainer
      alternate={[
        { href: '/about-us/', lang: 'en-gb' },
        { href: '/au/about-us/', lang: 'en-au' },
        { href: '/about-us/', lang: 'x-default' },
      ]}
      breadcrumb={breadcrumb}
      seoProps={seoProps}
    >
      <main className='overflow-x-hidden new-design d-flex flex-column'>
        <div className='container-fluid ps-4 ps-lg-26 pe-4 pt-6 pt-lg-0'>
          <div className='d-flex flex-column flex-lg-row'>
            <div className='d-flex flex-column flex-1 pt-lg-0'>
              <HoxtonMixLogo className='mb-5 mb-lg-8 mt-lg-15 max-w-100' />
              <div className='d-flex flex-column text-center text-lg-start pb-lg-7'>
                <h1 className='fs-fluid-34-54 lh-114 me-lg-n44 mt-0 mb-0'>
                  <span className='color-pink2'>Established in 2010</span>, The
                  Hoxton Mix is not your typical virtual office provider.
                </h1>

                <div className='lh-114 lh-lg-157 fs-fluid-24-28 me-lg-n18'>
                  With a passion for innovation and a drive to empower SMEs
                  worldwide, we've become a leading name in the industry —
                  headquartered in the heart of London, United Kingdom.
                </div>
              </div>
            </div>

            <div
              className={classNames('d-flex flex-column align-items-center')}
              style={{
                width: size.isLgOrHigher ? '70%' : '100%',
              }}
            >
              {!playing && (
                <img
                  {...generateSrcSet(
                    '/images/new/au/robot-open-arm.png',
                    1191,
                    700,
                  )}
                  height={700}
                  width={1191}
                  alt='HaiRo™ AI Mail Management'
                  className='h-100 img-fluid object-fit-contain position-absolute z-index-1'
                  loading='eager'
                />
              )}
              <ReactPlayer
                style={{
                  zIndex: -1,
                }}
                className={classNames('w-full')}
                url={require('public/video/robot-open-arm.mp4')}
                width={'100%'}
                height={'100%'}
                controls={false}
                loop={true}
                volume={1}
                muted={true}
                playsinline={true}
                playing
                onStart={() => setPlaying(true)}
              />
            </div>
          </div>
        </div>
        <div className='container d-flex flex-column gap-10 mt-lg-10'>
          <Section
            title='Invent'
            description='Our tech-driven approach has allowed us to deliver a world-class SaaS virtual office service that enables businesses to maintain a prestigious London presence, regardless of location.'
            subDescription={
              <>
                We are proud to have served{' '}
                <strong className='color-pink2'>
                  over 100,000 customers globally to date
                </strong>
                , from emerging startups to well-established enterprises,
                providing leading <strong>B2B</strong> products and services
                that enhance business value.
              </>
            }
            image={{
              alt: 'Robot on scan background holding phone',
              height: 960,
              src: '/images/new/robot-holding-phone-scanbg.png',
              width: 624,
            }}
            imagePosition='right'
          />
          <Section
            title='Unite'
            description={`But that's not all. In 2018, we introduced Konnect, an advanced application programming interface that simplifies the delivery of virtual office services to enterprise-level organisations.`}
            subDescription={
              <>
                This cutting-edge technology effectively addresses storage,
                delivery, and customer service concerns, making virtual office
                solutions more accessible and streamlined than ever before.
                <br />
                <br />
                And we don't stop there. We have also created a dynamic
                ecosystem through the Hoxton Mix Collective, now rebranded as
                1UP. This initiative has enriched our community, cultivating an
                environment where expertise and innovation are always within
                reach. From diverse teams to innovative startups, projects come
                to life through the power of collaboration.
              </>
            }
            image={{
              alt: 'Enterprise robot with thunder sticker',
              height: 1098,
              src: '/images/new/au/enterprise-robot-with-thunder.png',
              width: 535,
            }}
            imagePosition='left'
          />
          <Section
            title='Inspire'
            description={`Our commitment to innovation and entrepreneurship has been recognised on numerous occasions.`}
            subDescription={
              <>
                Most notably, our directors were invited to a prestigious{' '}
                <Link
                  href='https://www.hoxtonmix.com/blog/hoxton-mix-directors-invited-to-reception-at-buckingham-palace/'
                  className='color-pink2 fw-bold text-decoration-none'
                >
                  Buckingham Palace Reception
                </Link>{' '}
                celebrating UK Innovation and Entrepreneurship, highlighting our
                impact on the industry. We were also invited to a{' '}
                <Link
                  href='https://www.hoxtonmix.com/blog/hoxton-mix-y-combinator-interview/'
                  className='color-pink2 fw-bold text-decoration-none'
                >
                  Y Combinator
                </Link>{' '}
                for our Virtual Office SaaS solution, showcasing the value and
                potential of our cutting-edge solutions in the global market.
              </>
            }
            image={{
              alt: 'Enterprise robot with thunder sticker',
              height: 945,
              src: '/images/new/robot-holding-mail.png',
              width: 733,
            }}
            imagePosition='right'
          />
          <Section
            title='Acquire'
            contentContainerClassName='pt-lg-45'
            description={
              <>
                In <strong>2023</strong>, we strengthened our portfolio with the
                strategic acquisitions of <strong>Square Circle</strong> and{' '}
                <strong>Solid State</strong>.
              </>
            }
            subDescription={
              <div className='d-flex flex-column gap-7'>
                These alliances significantly enhanced our capabilities in
                developing top-tier digital products. By merging their expertise
                with ours, these acquisitions marked another milestone in our
                journey of continuous innovation and commitment to empowering
                businesses globally.
                <div className='d-flex flex-column align-items-center align-items-lg-start mb-4 mb-lg-0 gap-7'>
                  <Partner
                    icon={<SolidStateGroup />}
                    name='Solid State Group'
                    href='https://solidstategroup.com/'
                  />
                  <Partner
                    icon={<SquareCircle />}
                    name='Square Circle'
                    href='https://www.sqcircle.com/'
                  />
                </div>
              </div>
            }
            image={{
              alt: 'Enterprise robot with thunder sticker',
              height: 1370,
              src: '/images/new/au/robot-pointing-at-mail-speedbg.png',
              width: 703,
            }}
            imagePosition='left'
          />
          <Section
            title='Transcend'
            image={{
              alt: 'Enterprise robot with thunder sticker',
              height: 1061,
              src: '/images/new/vo-robot-holding-phone.png',
              width: 696,
            }}
            imagePosition='right'
            description={`At Hoxton Mix, our primary objective is to streamline the process of starting and managing businesses.`}
            subDescription={`With our passion for innovation, commitment to excellence, and drive to empower SMEs worldwide, we proudly offer virtual office solutions that genuinely make a difference. Join us today and discover how we can help you take your business to the next level.`}
          />
        </div>
      </main>
    </SeoContainer>
  )
}

Page.getLayout = (page: React.ReactNode) => (
  <PageContainer>{page}</PageContainer>
)

export async function getStaticProps(
  context: I18NConfig & { locale?: string; params: { uid: string } },
) {
  try {
    const client = createClient(context)
    const lang = Utils.getLocaleID(context.locale || context.defaultLocale)
    const plans = await getPlans()
    return {
      props: {
        plans,
      },
    }
  } catch (e) {
    console.log(e)
    return {
      props: {},
    }
  }
}
export default Page
